<script setup lang="ts">
interface Props {
  modelValue: boolean
  originalFilename: string
  selectedFiles: File[]
  loading?: boolean
  errorMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  errorMessage: '',
})

interface Emit {
  (event: 'update:modelValue', value: boolean): void
  (event: 'update:selectedFiles', value: File[]): void
  (event: 'confirm'): void
}

const emit = defineEmits<Emit>()

const closeDialog = () => {
  if (!props.loading) {
    emit('update:modelValue', false)
  }
}

const onFilesChange = (files: File[] | File | null) => {
  emit('update:selectedFiles', Array.isArray(files) ? files.slice(0, 1) : files ? [files] : [])
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    :persistent="loading"
    max-width="560"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between">
        <span>Re-upload file</span>
        <VBtn
          icon="mdi-close"
          variant="text"
          size="small"
          :disabled="loading"
          @click="closeDialog"
        />
      </VCardTitle>

      <VCardText>
        <div class="text-body-2 mb-4">
          Choose one replacement for <strong>{{ originalFilename }}</strong>.
        </div>

        <VFileInput
          :model-value="selectedFiles"
          label="Select replacement file"
          prepend-icon="tabler-paperclip"
          chips
          show-size
          :disabled="loading"
          @update:model-value="onFilesChange"
        />

        <VAlert
          v-if="errorMessage"
          type="error"
          variant="tonal"
          class="mt-3"
        >
          {{ errorMessage }}
        </VAlert>
      </VCardText>

      <VCardActions>
        <VSpacer />
        <VBtn
          variant="text"
          :disabled="loading"
          @click="closeDialog"
        >
          Cancel
        </VBtn>
        <VBtn
          color="primary"
          :loading="loading"
          :disabled="loading || selectedFiles.length !== 1"
          @click="emit('confirm')"
        >
          Re-upload
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
